import React, { useState, useEffect, useRef, useMemo } from 'react';
import axiosInstance from '../config/axios';
import { Pencil, Trash2, ChevronDown, Search, Check, Download } from 'lucide-react';
import './InventoryManagement.css';
import { friendlyItemLabel, compareItemsByFriendlyDisplay, inventoryTextSearchScore } from '../utils/inventoryDisplay';
import InventoryFieldCombobox, { collectInventoryFieldSuggestions } from './InventoryFieldCombobox';
import { parseThresholdMap } from '../utils/countReviewFlags';
import {
  discoverSiteClusters,
  buildThresholdMapFromInputs,
  thresholdMapToInputs
} from '../utils/siteClusters';
import { EXPORT_FAILED_MESSAGE, getFriendlyErrorMessage } from '../utils/errorMessages';

/** Pack Size, Unit 1, Unit 2 are stored/displayed in uppercase (e.g. Case → CASE, 1 Kg → 1 KG). */
function toUppercaseInventoryField(value) {
  if (value == null || value === '') return '';
  return String(value).toUpperCase();
}

function hasDuplicateVendorItemCode(items, brandId, code, excludeItemId) {
  const trimmed = (code || '').trim();
  if (!trimmed) return false;
  const key = trimmed.toLowerCase();
  const pool = brandId
    ? (items || []).filter((i) => String(i.brandId) === String(brandId))
    : (items || []).filter((i) => !i.brandId);
  return pool.some((i) => {
    if (excludeItemId != null && String(i.id) === String(excludeItemId)) return false;
    const existing = (i.vendorItemCode || '').trim();
    return existing && existing.toLowerCase() === key;
  });
}

/** Mirrors the backend per-brand create duplicate rule; returns the matching field label or null. */
function findNewItemDuplicateInBrand(items, brandId, data) {
  const norm = (v) => (v != null ? String(v) : '').trim().toLowerCase();
  const name = norm(data.itemName);
  const desc = norm(data.itemDescription);
  const code = norm(data.vendorItemCode);
  for (const i of items || []) {
    if (String(i.brandId) !== String(brandId)) continue;
    if (name && norm(i.itemName ?? i.name) === name) return 'Item Name';
    if (desc && norm(i.itemDescription) === desc) return 'Item Description';
    if (code && norm(i.vendorItemCode) === code) return 'Vendor Item Code';
  }
  return null;
}

function hasNumericValue(value) {
  return value !== '' && value != null && !Number.isNaN(parseFloat(value));
}

/** @returns {string|null} first validation error */
function validateInventoryItemForm(data) {
  if (!data.itemName || !String(data.itemName).trim()) {
    return 'Item Name is required';
  }
  if (!(data.vendorItemCode || '').trim()) {
    return 'Vendor Item Code is required';
  }
  if (!(data.packSize || '').trim()) {
    return 'Pack Size is required';
  }
  if (!(data.unit1Name || '').trim()) {
    return 'Unit 1 is required';
  }
  if (!hasNumericValue(data.value1)) {
    return 'Value 1 is required';
  }
  const hasUnit2 = !!(data.unit2Name || '').trim();
  const hasValue2 = hasNumericValue(data.value2);
  if (hasUnit2 && !hasValue2) {
    return 'Value 2 is required when Unit 2 is specified';
  }
  if (hasValue2 && !hasUnit2) {
    return 'Unit 2 is required when Value 2 is specified';
  }
  return null;
}


const MULTI_BRAND_COPYABLE_FIELDS_UI = [
  'itemName',
  'itemDescription',
  'pivotCategoryId',
  'unit1Id',
  'unit2Id',
  'value1',
  'value2',
  'vendor',
  'packSize',
  'vendorItemCode',
  'location',
  'priority',
  'valueThreshold',
  'valueThresholdsByCluster'
];

const MULTI_BRAND_FIELD_LABELS = {
  itemName: 'Item Name',
  itemDescription: 'Item Description',
  pivotCategoryId: 'Type',
  unit1Id: 'Unit 1',
  unit2Id: 'Unit 2',
  value1: 'Value 1',
  value2: 'Value 2',
  vendor: 'Vendor',
  packSize: 'Pack Size',
  vendorItemCode: 'Vendor Item Code',
  location: 'Location',
  priority: 'Priority',
  valueThreshold: 'High Value Threshold',
  valueThresholdsByCluster: 'Cluster Thresholds'
};

function formatFieldDisplay(value) {
  if (value == null || value === '') return '(empty)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_) {
      return String(value);
    }
  }
  return String(value);
}

function buildChangedCopyableSummary(editingItem, submitData, formData) {
  if (!editingItem || !submitData) return [];
  const before = {
    itemName: editingItem.itemName || editingItem.name || '',
    itemDescription: editingItem.itemDescription || '',
    pivotCategoryId: editingItem.pivotCategoryId || null,
    unit1Id: editingItem.unit1Id || null,
    unit2Id: editingItem.unit2Id || null,
    value1: editingItem.value1 ?? null,
    value2: editingItem.value2 ?? null,
    vendor: editingItem.vendor || '',
    packSize: editingItem.packSize || '',
    vendorItemCode: editingItem.vendorItemCode || '',
    location: editingItem.location || '',
    priority: Boolean(editingItem.priority),
    valueThreshold: editingItem.valueThreshold ?? null,
    valueThresholdsByCluster: editingItem.valueThresholdsByCluster ?? null
  };
  const rows = [];
  for (const field of MULTI_BRAND_COPYABLE_FIELDS_UI) {
    if (!Object.prototype.hasOwnProperty.call(submitData, field)) continue;
    const after = submitData[field];
    const prev = before[field];
    const same =
      field === 'priority'
        ? Boolean(prev) === Boolean(after)
        : field === 'valueThresholdsByCluster'
          ? JSON.stringify(prev || '') === JSON.stringify(after || '')
          : String(prev ?? '') === String(after ?? '');
    if (same) continue;
    let fromLabel = formatFieldDisplay(prev);
    let toLabel = formatFieldDisplay(after);
    if (field === 'pivotCategoryId') {
      fromLabel = editingItem.pivotCategory || fromLabel;
      toLabel = (formData && formData.pivotCategoryName) || toLabel;
    } else if (field === 'unit1Id') {
      fromLabel = editingItem.unit1 || fromLabel;
      toLabel = (formData && formData.unit1Name) || toLabel;
    } else if (field === 'unit2Id') {
      fromLabel = editingItem.unit2 || fromLabel;
      toLabel = (formData && formData.unit2Name) || toLabel;
    }
    rows.push({
      field,
      label: MULTI_BRAND_FIELD_LABELS[field] || field,
      from: fromLabel,
      to: toLabel
    });
  }
  return rows;
}

const EMPTY_ITEM_FORM = {
  itemName: '',
  itemDescription: '',
  pivotCategoryName: '',
  unit1Name: '',
  unit2Name: '',
  brandId: '',
  value1: '',
  value2: '',
  location: '',
  priority: false,
  vendor: '',
  packSize: '',
  vendorItemCode: '',
  valueThreshold: '',
  valueThresholdsByCluster: {}
};

const InventoryManagement = ({ inventory, allInventory = [], onCreateItem, onRefresh, selectedBrand, brands, sites = [], isAdmin }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [globalInventoryValueLimit, setGlobalInventoryValueLimit] = useState('');
  const [globalClusterInputs, setGlobalClusterInputs] = useState({});
  const [globalLimitSaving, setGlobalLimitSaving] = useState(false);
  const [globalLimitSaveSuccess, setGlobalLimitSaveSuccess] = useState(false);
  const [globalLimitError, setGlobalLimitError] = useState('');
  const [itemClusterExpanded, setItemClusterExpanded] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_ITEM_FORM });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [inventoryExporting, setInventoryExporting] = useState(false);
  const [multiBrandModal, setMultiBrandModal] = useState(null);
  const [multiBrandSelectedIds, setMultiBrandSelectedIds] = useState([]);
  const [multiBrandSaving, setMultiBrandSaving] = useState(false);
  const [additionalBrandIds, setAdditionalBrandIds] = useState([]);
  const [createConfirm, setCreateConfirm] = useState(null);
  const [createSaving, setCreateSaving] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    if (selectedBrand) {
      setFormData(prev => ({ ...prev, brandId: selectedBrand }));
    }
  }, [selectedBrand, showCreateForm]);

  const primaryBrandIdForCreate = formData.brandId || selectedBrand || '';

  useEffect(() => {
    setAdditionalBrandIds((prev) => {
      const next = prev.filter((id) => id !== String(primaryBrandIdForCreate));
      return next.length === prev.length ? prev : next;
    });
  }, [primaryBrandIdForCreate]);

  const additionalBrandCandidates = useMemo(() => {
    if (!primaryBrandIdForCreate) return [];
    return (brands || []).filter((b) => String(b.id) !== String(primaryBrandIdForCreate));
  }, [brands, primaryBrandIdForCreate]);

  useEffect(() => {
    let cancelled = false;
    axiosInstance.get('/api/tenant/settings')
      .then(res => {
        if (cancelled) return;
        const settings = res.data?.settings || {};
        const v = settings.globalInventoryValueLimit;
        setGlobalInventoryValueLimit(v !== undefined && v !== null && v !== '' ? String(v) : '');
        const mapRaw = settings.globalInventoryValueLimitsByCluster;
        const parsed = parseThresholdMap(mapRaw);
        const clusters = discoverSiteClusters(sites, parsed);
        setGlobalClusterInputs(thresholdMapToInputs(parsed, clusters));
      })
      .catch(() => { if (!cancelled) setGlobalLimitError(''); });
    return () => { cancelled = true; };
  }, [sites]);

  const siteClusters = useMemo(() => {
    const globalParsed = {};
    for (const c of discoverSiteClusters(sites, {})) {
      const raw = globalClusterInputs[c.key];
      if (raw !== '' && raw != null) {
        const n = parseFloat(String(raw));
        if (!Number.isNaN(n)) globalParsed[c.key] = n;
      }
    }
    return discoverSiteClusters(sites, globalParsed);
  }, [sites, globalClusterInputs]);

  useEffect(() => {
    setGlobalClusterInputs((prev) => {
      const merged = { ...prev };
      for (const c of siteClusters) {
        if (merged[c.key] === undefined) merged[c.key] = '';
      }
      return merged;
    });
  }, [siteClusters]);

  useEffect(() => {
    if (!showCreateForm) return;
    setFormData((prev) => {
      const next = { ...(prev.valueThresholdsByCluster || {}) };
      let changed = false;
      for (const c of siteClusters) {
        if (next[c.key] === undefined) {
          next[c.key] = '';
          changed = true;
        }
      }
      return changed ? { ...prev, valueThresholdsByCluster: next } : prev;
    });
  }, [siteClusters, showCreateForm]);

  const handleSaveGlobalLimit = async () => {
    setGlobalLimitError('');
    setGlobalLimitSaving(true);
    setGlobalLimitSaveSuccess(false);
    try {
      const raw = globalInventoryValueLimit.trim();
      if (raw !== '') {
        const num = parseFloat(raw);
        if (Number.isNaN(num) || num < 0) {
          setGlobalLimitError('Default global threshold must be a non-negative number or empty');
          return;
        }
      }
      const clusterPayload = buildThresholdMapFromInputs(globalClusterInputs, siteClusters);
      if (clusterPayload.error) {
        setGlobalLimitError(clusterPayload.error);
        return;
      }
      await axiosInstance.put('/api/tenant/settings', {
        globalInventoryValueLimit: raw === '' ? '' : raw,
        globalInventoryValueLimitsByCluster:
          Object.keys(clusterPayload.value).length > 0 ? clusterPayload.value : ''
      });
      setGlobalLimitSaveSuccess(true);
      setTimeout(() => setGlobalLimitSaveSuccess(false), 2000);
    } catch (err) {
      setGlobalLimitError(err.response?.data?.error || 'Failed to save');
    } finally {
      setGlobalLimitSaving(false);
    }
  };

  const resolveOrCreateCategory = async (name) => {
    if (!name || !name.trim()) return null;
    const trimmed = name.trim();
    const res = await axiosInstance.get('/api/tenant/categories');
    const categories = res.data.categories || [];
    const found = categories.find(c => (c.name || '').toLowerCase() === trimmed.toLowerCase());
    if (found) return found.id;
    const createRes = await axiosInstance.post('/api/tenant/categories', { name: trimmed });
    return createRes.data?.id ?? createRes.data?.category?.id ?? null;
  };

  const resolveOrCreateUnit = async (name) => {
    if (!name || !name.trim()) return null;
    const trimmed = name.trim();
    const res = await axiosInstance.get('/api/tenant/units');
    const units = res.data.units || [];
    const found = units.find(u => (u.name || '').toLowerCase() === trimmed.toLowerCase());
    if (found) return found.id;
    const createRes = await axiosInstance.post('/api/tenant/units', { name: trimmed });
    return createRes.data?.id ?? createRes.data?.unit?.id ?? null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const finalFormData = {
      ...formData,
      brandId: formData.brandId || selectedBrand || '',
      priority: Boolean(formData.priority),
      unit1Name: toUppercaseInventoryField(formData.unit1Name),
      unit2Name: toUppercaseInventoryField(formData.unit2Name),
      packSize: toUppercaseInventoryField(formData.packSize)
    };

    setFormData((prev) => ({
      ...prev,
      unit1Name: finalFormData.unit1Name,
      unit2Name: finalFormData.unit2Name,
      packSize: finalFormData.packSize
    }));

    const validationError = validateInventoryItemForm(finalFormData);
    if (validationError) {
      setError(validationError);
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    const vendorCode = (finalFormData.vendorItemCode || '').trim();
    if (hasDuplicateVendorItemCode(inventory, finalFormData.brandId, vendorCode, editingItem?.id)) {
      setError(
        'Vendor Item Code must be unique for this brand. An item with this code already exists.'
      );
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    let pivotCategoryId = null;
    let unit1Id = null;
    let unit2Id = null;
    try {
      pivotCategoryId = await resolveOrCreateCategory(finalFormData.pivotCategoryName);
      unit1Id = await resolveOrCreateUnit(finalFormData.unit1Name);
      if (finalFormData.unit2Name && finalFormData.unit2Name.trim()) {
        unit2Id = await resolveOrCreateUnit(finalFormData.unit2Name);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resolve type or units');
      return;
    }

    if (!unit1Id) {
      setError('Unit 1 is required');
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    const itemClusterPayload = buildThresholdMapFromInputs(
      formData.valueThresholdsByCluster,
      siteClusters
    );
    if (itemClusterPayload.error) {
      setError(itemClusterPayload.error);
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    const submitData = {
      itemName: finalFormData.itemName.trim(),
      itemDescription: finalFormData.itemDescription != null ? String(finalFormData.itemDescription) : '',
      pivotCategoryId,
      unit1Id,
      unit2Id,
      brandId: finalFormData.brandId || null,
      value1: finalFormData.value1 ?? '',
      value2: hasNumericValue(finalFormData.value2) ? finalFormData.value2 : '',
      location: finalFormData.location ?? '',
      priority: Boolean(finalFormData.priority),
      vendor: finalFormData.vendor ?? '',
      packSize: finalFormData.packSize ?? '',
      vendorItemCode: vendorCode,
      valueThreshold: finalFormData.valueThreshold && finalFormData.valueThreshold.trim() !== '' ? parseFloat(finalFormData.valueThreshold) : null,
      valueThresholdsByCluster:
        Object.keys(itemClusterPayload.value).length > 0 ? itemClusterPayload.value : ''
    };

    console.log('Submitting inventory item with data:', submitData);

    if (editingItem) {
      try {
        const matchRes = await axiosInstance.get(
          `/api/tenant/inventory/${editingItem.id}/matching-brands`
        );
        const matches = matchRes.data?.matches || [];
        if (!matches.length) {
          await axiosInstance.put(`/api/tenant/inventory/${editingItem.id}`, submitData);
          setSuccess('Inventory item updated successfully');
          setFormData({ ...EMPTY_ITEM_FORM, brandId: selectedBrand || '' });
          setItemClusterExpanded(false);
          setEditingItem(null);
          setShowCreateForm(false);
          setTimeout(() => window.location.reload(), 1000);
          return;
        }
        const currentBrand =
          (brands || []).find((b) => String(b.id) === String(editingItem.brandId || submitData.brandId)) ||
          null;
        setMultiBrandSelectedIds([]);
        setMultiBrandModal({
          submitData,
          matches,
          warning:
            matchRes.data?.warning ||
            'Only items that appear to match this inventory item are shown. Please review carefully before applying changes to other brands.',
          changedSummary: buildChangedCopyableSummary(editingItem, submitData, finalFormData),
          currentBrandName: currentBrand?.name || '(No Brand)',
          currentItemName: editingItem.itemName || editingItem.name || submitData.itemName || '',
          currentVendorItemCode: editingItem.vendorItemCode || submitData.vendorItemCode || ''
        });
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to update item');
      }
    } else {
      const candidateIds = new Set(additionalBrandCandidates.map((b) => String(b.id)));
      const selectedAdditional = additionalBrandIds.filter((id) => candidateIds.has(id));
      if (selectedAdditional.length > 0) {
        const brandName = (id) =>
          (brands || []).find((b) => String(b.id) === String(id))?.name || String(id);
        const duplicateLines = selectedAdditional
          .map((id) => {
            const field = findNewItemDuplicateInBrand(allInventory, id, submitData);
            return field ? `${brandName(id)} (same ${field})` : null;
          })
          .filter(Boolean);
        if (duplicateLines.length > 0) {
          setError(
            `This item already exists in: ${duplicateLines.join(', ')}. Unselect those brands or change the item details.`
          );
          if (formRef.current) {
            formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          return;
        }
        setCreateConfirm({
          submitData: { ...submitData, additionalBrandIds: selectedAdditional },
          brandNames: [brandName(submitData.brandId), ...selectedAdditional.map(brandName)]
        });
        return;
      }
      try {
        console.log('Submitting inventory item:', submitData);
        const result = await onCreateItem(submitData);
        console.log('Create result:', result);
        if (result && result.success) {
          setSuccess('Inventory item created successfully');
          setFormData({ ...EMPTY_ITEM_FORM, brandId: selectedBrand || '' });
        setItemClusterExpanded(false);
          setShowCreateForm(false);
          setTimeout(() => window.location.reload(), 1000);
        } else {
          setError(result?.error || 'Failed to create inventory item. Please try again.');
        }
      } catch (error) {
        console.error('Error creating inventory item:', error);
        setError(error.response?.data?.error || error.message || 'Failed to create inventory item. Please try again.');
      }
    }
  };


  const closeMultiBrandModal = () => {
    if (multiBrandSaving) return;
    setMultiBrandModal(null);
    setMultiBrandSelectedIds([]);
  };

  const toggleAdditionalBrand = (brandId) => {
    const id = String(brandId);
    setAdditionalBrandIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const cancelCreateConfirm = () => {
    if (createSaving) return;
    setCreateConfirm(null);
  };

  const handleConfirmMultiBrandCreate = async () => {
    if (!createConfirm || createSaving) return;
    setCreateSaving(true);
    setError('');
    try {
      const result = await onCreateItem(createConfirm.submitData);
      if (result && result.success) {
        setCreateConfirm(null);
        setSuccess(`Inventory item created in ${createConfirm.brandNames.length} brands`);
        setFormData({ ...EMPTY_ITEM_FORM, brandId: selectedBrand || '' });
        setAdditionalBrandIds([]);
        setItemClusterExpanded(false);
        setShowCreateForm(false);
        setTimeout(() => window.location.reload(), 1000);
      } else {
        setCreateConfirm(null);
        setError(result?.error || 'Failed to create inventory item. Please try again.');
      }
    } catch (error) {
      setCreateConfirm(null);
      setError(error.response?.data?.error || error.message || 'Failed to create inventory item. Please try again.');
    } finally {
      setCreateSaving(false);
    }
  };

  const finishInventoryUpdateSuccess = (message) => {
    setSuccess(message || 'Inventory item updated successfully');
    setFormData({ ...EMPTY_ITEM_FORM, brandId: selectedBrand || '' });
    setItemClusterExpanded(false);
    setEditingItem(null);
    setShowCreateForm(false);
    setMultiBrandModal(null);
    setMultiBrandSelectedIds([]);
    setTimeout(() => window.location.reload(), 1000);
  };

  const handleSaveCurrentBrandOnly = async () => {
    if (!editingItem || !multiBrandModal) return;
    setMultiBrandSaving(true);
    setError('');
    try {
      await axiosInstance.put(
        `/api/tenant/inventory/${editingItem.id}`,
        multiBrandModal.submitData
      );
      finishInventoryUpdateSuccess('Inventory item updated successfully');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update item');
    } finally {
      setMultiBrandSaving(false);
    }
  };

  const handleApplyToSelectedBrands = async () => {
    if (!editingItem || !multiBrandModal || multiBrandSelectedIds.length === 0) return;
    setMultiBrandSaving(true);
    setError('');
    try {
      const res = await axiosInstance.put(
        `/api/tenant/inventory/${editingItem.id}/multi-brand-update`,
        {
          ...multiBrandModal.submitData,
          applyToBrandItemIds: multiBrandSelectedIds
        }
      );
      finishInventoryUpdateSuccess(
        res.data?.message || 'Inventory item updated across selected brands'
      );
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update item across brands');
    } finally {
      setMultiBrandSaving(false);
    }
  };

  const toggleMultiBrandSelection = (itemId) => {
    const id = String(itemId);
    setMultiBrandSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleEdit = (item) => {
    const itemClusterMap = parseThresholdMap(item.valueThresholdsByCluster);
    const clustersForItem = discoverSiteClusters(sites, itemClusterMap);
    const clusterInputs = thresholdMapToInputs(itemClusterMap, clustersForItem);
    setEditingItem(item);
    setFormData({
      itemName: item.name || '',
      itemDescription: item.itemDescription || '',
      pivotCategoryName: item.pivotCategory || '',
      unit1Name: toUppercaseInventoryField(item.unit1 || ''),
      unit2Name: toUppercaseInventoryField(item.unit2 || ''),
      brandId: item.brandId || '',
      value1: (item.value1) ?? '',
      value2: (item.value2) ?? '',
      location: item.location || '',
      priority: item.priority || false,
      vendor: item.vendor || '',
      packSize: toUppercaseInventoryField(item.packSize || ''),
      vendorItemCode: item.vendorItemCode || '',
      valueThreshold: item.valueThreshold != null && item.valueThreshold !== '' ? String(item.valueThreshold) : '',
      valueThresholdsByCluster: clusterInputs
    });
    setItemClusterExpanded(Object.keys(itemClusterMap).length > 0);
    setAdditionalBrandIds([]);
    setShowCreateForm(true);
    setError('');
    setSuccess('');
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleCancel = () => {
    setEditingItem(null);
    setShowCreateForm(false);
    setAdditionalBrandIds([]);
    setCreateConfirm(null);
    setFormData({ ...EMPTY_ITEM_FORM, brandId: selectedBrand || '' });
    setItemClusterExpanded(false);
    setError('');
    setSuccess('');
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getFilteredInventory = () => {
    if (!searchTerm.trim()) return inventory;
    const q = searchTerm.toLowerCase().trim();
    return inventory.filter(item => {
      return (item.name || '').toLowerCase().includes(q) ||
             (item.itemDescription || '').toLowerCase().includes(q) ||
             (item.pivotCategory || '').toLowerCase().includes(q) ||
             (item.vendor || '').toLowerCase().includes(q) ||
             (item.location || '').toLowerCase().includes(q) ||
             (item.vendorItemCode || '').toLowerCase().includes(q) ||
             (item.unit1 || '').toLowerCase().includes(q) ||
             (item.packSize || '').toLowerCase().includes(q);
    });
  };

  const getSortedInventory = () => {
    const filtered = getFilteredInventory();
    const q = searchTerm.trim() ? searchTerm.toLowerCase().trim() : '';
    if (!sortColumn) {
      if (!q) return filtered;
      return [...filtered].sort((a, b) => {
        const ra = inventoryTextSearchScore(a, q);
        const rb = inventoryTextSearchScore(b, q);
        if (rb !== ra) return rb - ra;
        return compareItemsByFriendlyDisplay(a, b);
      });
    }
    return [...filtered].sort((a, b) => {
      let aValue, bValue;
      switch (sortColumn) {
        case 'name': {
          const cmp = compareItemsByFriendlyDisplay(a, b);
          if (cmp < 0) return sortDirection === 'asc' ? -1 : 1;
          if (cmp > 0) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }
        case 'category':
        case 'pivotCategory':
          aValue = (a.pivotCategory || '').toLowerCase();
          bValue = (b.pivotCategory || '').toLowerCase();
          break;
        case 'unit1':
          aValue = (a.unit1 || '').toLowerCase();
          bValue = (b.unit1 || '').toLowerCase();
          break;
        case 'value1':
          aValue = parseFloat(a.value1 || 0);
          bValue = parseFloat(b.value1 || 0);
          break;
        case 'unit2':
          aValue = (a.unit2 || '').toLowerCase();
          bValue = (b.unit2 || '').toLowerCase();
          break;
        case 'value2':
          aValue = parseFloat(a.value2 || 0);
          bValue = parseFloat(b.value2 || 0);
          break;
        case 'location':
          aValue = (a.location || '').toLowerCase();
          bValue = (b.location || '').toLowerCase();
          break;
        case 'vendor':
          aValue = (a.vendor || '').toLowerCase();
          bValue = (b.vendor || '').toLowerCase();
          break;
        case 'packSize':
          aValue = (a.packSize || '').toLowerCase();
          bValue = (b.packSize || '').toLowerCase();
          break;
        case 'priority':
          aValue = a.priority ? 1 : 0;
          bValue = b.priority ? 1 : 0;
          break;
        case 'valueThreshold':
          aValue = (a.valueThreshold != null && a.valueThreshold !== '') ? parseFloat(a.valueThreshold) : -Infinity;
          bValue = (b.valueThreshold != null && b.valueThreshold !== '') ? parseFloat(b.valueThreshold) : -Infinity;
          break;
        default:
          return 0;
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this inventory item?')) return;
    try {
      await axiosInstance.delete(`/api/tenant/inventory/${itemId}`);
      setSuccess('Inventory item deleted successfully');
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete item');
    }
  };

  const handleExportInventoryExcel = async () => {
    if (!selectedBrand) {
      setError('Please select a brand first');
      return;
    }
    setInventoryExporting(true);
    setError('');
    try {
      const base = axiosInstance.defaults.baseURL || '';
      const brandName = brands.find((b) => b.id === selectedBrand)?.name || 'Brand';
      const url =
        base +
        '/api/tenant/inventory/export-excel?brandId=' +
        encodeURIComponent(selectedBrand);
      const token = localStorage.getItem('token');
      const fetchHeaders = { Authorization: 'Bearer ' + token };
      if (typeof window !== 'undefined' && window.location.host) {
        fetchHeaders['X-Tenant-Host'] = window.location.host;
      }
      const res = await fetch(url, { headers: fetchHeaders });
      if (!res.ok) {
        let errData = null;
        try {
          errData = await res.json();
        } catch (_) {}
        setError(
          getFriendlyErrorMessage(
            { response: { status: res.status, data: errData || { code: 'EXPORT_FAILED' } } },
            { kind: 'export' }
          ) || EXPORT_FAILED_MESSAGE
        );
        return;
      }
      const blob = await res.blob();
      const safeBrand = brandName.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_').slice(0, 40) || 'Brand';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download =
        'Inventory_Export_' + safeBrand + '_' + new Date().toISOString().split('T')[0] + '.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error(err);
      setError(getFriendlyErrorMessage(err, { kind: 'export' }) || EXPORT_FAILED_MESSAGE);
    } finally {
      setInventoryExporting(false);
    }
  };

  const handleClearAllItems = async () => {
    if (!selectedBrand) { setError('Please select a brand first'); return; }
    const brandName = brands.find(b => b.id === selectedBrand)?.name || 'this brand';
    const itemCount = inventory.length;
    if (itemCount === 0) { setError('No items to clear'); return; }
    if (!window.confirm(`Are you sure you want to delete ALL ${itemCount} inventory item${itemCount !== 1 ? 's' : ''} for ${brandName}?\n\nThis action cannot be undone!`)) return;
    if (!window.confirm(`FINAL CONFIRMATION: Delete all ${itemCount} item${itemCount !== 1 ? 's' : ''} for ${brandName}?`)) return;
    try {
      setError(''); setSuccess('');
      const response = await axiosInstance.delete(`/api/tenant/inventory/clear-by-brand/${selectedBrand}`);
      const deletedCount = response.data.deletedCount || itemCount;
      setSuccess(response.data.message || `Successfully deleted all ${deletedCount} inventory item${deletedCount !== 1 ? 's' : ''} for ${brandName}`);
      setTimeout(() => { window.location.reload(); }, 1000);
    } catch (error) {
      console.error('Error clearing inventory:', error);
      setError(error.response?.data?.error || 'Failed to clear inventory items.');
      if (onRefresh) { setTimeout(() => onRefresh(), 100); }
    }
  };

  const SortableHeader = ({ column, children }) => (
    <th className="im-sortable-th" onClick={() => handleSort(column)} title={'Sort by ' + children}>
      <button type="button" className="im-sort-btn">
        <span>{children}</span>
        <ChevronDown size={10} className={'im-sort-icon' + (sortColumn === column ? ' active' : '') + (sortColumn === column && sortDirection === 'desc' ? ' desc' : '')} />
      </button>
    </th>
  );

  const priorityCount = inventory.filter(i => i.priority).length;
  const categorySet = new Set(inventory.map(i => i.pivotCategory).filter(Boolean));

  const brandIdForSuggestions = formData.brandId || selectedBrand || '';

  const inventoryForSuggestions = useMemo(() => {
    if (!brandIdForSuggestions) return inventory;
    return inventory.filter((item) => String(item.brandId) === String(brandIdForSuggestions));
  }, [inventory, brandIdForSuggestions]);

  const fieldSuggestions = useMemo(
    () => ({
      type: collectInventoryFieldSuggestions(inventoryForSuggestions, (i) => i.pivotCategory),
      unit1: collectInventoryFieldSuggestions(inventoryForSuggestions, (i) => i.unit1),
      unit2: collectInventoryFieldSuggestions(inventoryForSuggestions, (i) => i.unit2),
      vendor: collectInventoryFieldSuggestions(inventoryForSuggestions, (i) => i.vendor),
      packSize: collectInventoryFieldSuggestions(inventoryForSuggestions, (i) => i.packSize),
      location: collectInventoryFieldSuggestions(inventoryForSuggestions, (i) => i.location)
    }),
    [inventoryForSuggestions]
  );

  return (
    <div>
      <div className="im-section-header">
        <div>
          <h2 className="im-section-title">Inventory Management</h2>
          <p className="im-section-subtitle">Manage inventory items</p>
        </div>
        <div className="im-header-actions">
          {isAdmin && (
            <button
              type="button"
              className="btn btn-secondary im-export-btn"
              onClick={handleExportInventoryExcel}
              disabled={inventoryExporting || !selectedBrand}
              title={selectedBrand ? 'Download inventory for selected brand as Excel' : 'Select a brand to export inventory'}
            >
              <Download size={16} aria-hidden />
              {inventoryExporting ? 'Exporting…' : 'Export to Excel'}
            </button>
          )}
          <button className="btn btn-primary im-create-btn" onClick={() => {
            if (showCreateForm) { handleCancel(); } else {
              setShowCreateForm(true);
              setEditingItem(null);
              setAdditionalBrandIds([]);
              setFormData({ ...EMPTY_ITEM_FORM, brandId: selectedBrand || '' });
              setItemClusterExpanded(false);
            }
          }}>
            {showCreateForm ? 'Cancel' : 'Add Item'}
          </button>
          {selectedBrand && (
            <button className="btn btn-danger im-create-btn" onClick={handleClearAllItems} disabled={inventory.length === 0} title={inventory.length === 0 ? "No items to clear" : "Clear all inventory items for this brand"}>
              Clear All Items
            </button>
          )}
        </div>
      </div>

      <div className="im-global-threshold-card card">
        <div className="im-global-limit-row">
          <div className="im-global-limit-field">
            <label className="im-global-limit-label" htmlFor="im-global-limit-input">Default global threshold</label>
            <small className="im-threshold-help">Used when no cluster-specific global threshold applies.</small>
            <input
              id="im-global-limit-input"
              type="number"
              min="0"
              step="1"
              className="im-global-limit-input"
              placeholder="e.g. 10000"
              value={globalInventoryValueLimit}
              onChange={(e) => setGlobalInventoryValueLimit(e.target.value)}
              disabled={globalLimitSaving}
            />
          </div>
          <button
            type="button"
            className={'im-global-limit-save' + (globalLimitSaveSuccess ? ' im-global-limit-save-success' : '')}
            onClick={handleSaveGlobalLimit}
            disabled={globalLimitSaving}
            title="Save global thresholds"
            aria-label="Save global thresholds"
          >
            <Check size={20} strokeWidth={2.5} />
          </button>
        </div>

        <div className="im-cluster-threshold-section">
          <h4 className="im-cluster-section-title">Cluster-specific global thresholds</h4>
          {siteClusters.length === 0 ? (
            <p className="im-cluster-empty-msg">
              No site clusters found. Add cluster values to site information to configure cluster-specific thresholds.
            </p>
          ) : (
            <>
              <p className="im-threshold-help">
                Cluster thresholds override the default global threshold for sites in that cluster. Item-specific thresholds still override global thresholds.
              </p>
              <div className="im-cluster-threshold-grid">
                {siteClusters.map((cluster) => (
                  <div key={cluster.key} className="im-cluster-threshold-field">
                    <label htmlFor={`im-global-cluster-${cluster.key}`}>{cluster.label}</label>
                    <input
                      id={`im-global-cluster-${cluster.key}`}
                      type="number"
                      min="0"
                      step="1"
                      className="im-global-limit-input"
                      placeholder="Use default"
                      value={globalClusterInputs[cluster.key] ?? ''}
                      onChange={(e) =>
                        setGlobalClusterInputs((prev) => ({ ...prev, [cluster.key]: e.target.value }))
                      }
                      disabled={globalLimitSaving}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        {globalLimitError && <span className="im-global-limit-error">{globalLimitError}</span>}
      </div>

      <div className="im-stats-row">
        <div className="im-stat-card">
          <span className="im-stat-label">TOTAL ITEMS</span>
          <span className="im-stat-value im-stat-total">{inventory.length}</span>
        </div>
        <div className="im-stat-card">
          <span className="im-stat-label">PRIORITY ITEMS</span>
          <span className="im-stat-value im-stat-priority">{priorityCount}</span>
        </div>
        <div className="im-stat-card">
          <span className="im-stat-label">CATEGORIES</span>
          <span className="im-stat-value im-stat-categories">{categorySet.size}</span>
        </div>
      </div>

      {showCreateForm && (
        <form ref={formRef} onSubmit={handleSubmit} className="create-form card">
          <h4>{editingItem ? 'Edit Item' : 'Create New Item'}</h4>
          <div className="form-group">
            <label>Item Name *</label>
            <input type="text" value={formData.itemName} onChange={(e) => setFormData({ ...formData, itemName: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={formData.itemDescription} onChange={(e) => setFormData({ ...formData, itemDescription: e.target.value })} rows="3" />
          </div>
          {brands && brands.length > 0 && (
            <div className="form-group">
              <label>Brand {selectedBrand ? '(Pre-selected)' : '(Optional)'}</label>
              <select value={formData.brandId || ''} onChange={(e) => setFormData({ ...formData, brandId: e.target.value })} disabled={!!selectedBrand}>
                <option value="">No Brand</option>
                {brands.map(brand => (<option key={brand.id} value={brand.id}>{brand.name}</option>))}
              </select>
              {selectedBrand && (<small style={{ color: 'var(--color-text-muted)', fontSize: '12px', display: 'block', marginTop: '5px' }}>Brand is pre-selected based on your filter above</small>)}
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <InventoryFieldCombobox
                label="Type"
                value={formData.pivotCategoryName || ''}
                onChange={(v) => setFormData({ ...formData, pivotCategoryName: v })}
                suggestions={fieldSuggestions.type}
                placeholder="e.g. general, electronics"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Vendor Item Code *</label>
              <input type="text" value={formData.vendorItemCode} onChange={(e) => setFormData({ ...formData, vendorItemCode: e.target.value })} placeholder="Vendor Item Code" required />
            </div>
            <div className="form-group">
              <InventoryFieldCombobox
                label="Unit 1 *"
                value={formData.unit1Name || ''}
                onChange={(v) => setFormData({ ...formData, unit1Name: toUppercaseInventoryField(v) })}
                suggestions={fieldSuggestions.unit1}
                placeholder="e.g. PIECE, BOX, KG"
              />
            </div>
            <div className="form-group">
              <label>Value 1 *</label>
              <input type="number" step="0.0001" min="0" value={formData.value1} onChange={(e) => setFormData({ ...formData, value1: e.target.value })} placeholder="0.0000" required />
              <small style={{ color: 'var(--color-text-muted)', fontSize: '12px', display: 'block', marginTop: '5px' }}>This value is imported from the "Value1" column in Excel</small>
            </div>
            <div className="form-group">
              <InventoryFieldCombobox
                label="Unit 2"
                value={formData.unit2Name || ''}
                onChange={(v) => setFormData({ ...formData, unit2Name: toUppercaseInventoryField(v) })}
                suggestions={fieldSuggestions.unit2}
                placeholder="e.g. BOX, PALLET"
              />
              <small style={{ color: 'var(--color-text-muted)', fontSize: '12px', display: 'block', marginTop: '5px' }}>Imported from the "Unit2" column in Excel</small>
            </div>
            <div className="form-group">
              <label>Value 2</label>
              <input type="number" step="0.0001" min="0" value={formData.value2} onChange={(e) => setFormData({ ...formData, value2: e.target.value })} placeholder="0.0000" />
              <small style={{ color: 'var(--color-text-muted)', fontSize: '12px', display: 'block', marginTop: '5px' }}>This value is imported from the "Value2" column in Excel</small>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <InventoryFieldCombobox
                label="Vendor"
                value={formData.vendor}
                onChange={(v) => setFormData({ ...formData, vendor: v })}
                suggestions={fieldSuggestions.vendor}
                placeholder="Vendor name"
              />
            </div>
            <div className="form-group">
              <InventoryFieldCombobox
                label="Pack Size *"
                value={formData.packSize}
                onChange={(v) => setFormData({ ...formData, packSize: toUppercaseInventoryField(v) })}
                suggestions={fieldSuggestions.packSize}
                placeholder="e.g., 12, 24, 1 KG"
              />
            </div>
          </div>
          <div className="form-group">
            <InventoryFieldCombobox
              label="Location"
              value={formData.location}
              onChange={(v) => setFormData({ ...formData, location: v })}
              suggestions={fieldSuggestions.location}
              placeholder="e.g., Freezer, Fridge, Storage Room"
            />
            <small style={{ color: 'var(--color-text-muted)', fontSize: '12px', display: 'block', marginTop: '5px' }}>Location where this item is typically stored (e.g., Freezer, Fridge, Storage Room)</small>
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input type="checkbox" checked={Boolean(formData.priority)} onChange={(e) => setFormData({ ...formData, priority: e.target.checked })} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
              <span style={{ fontWeight: 'bold', color: '#dc3545' }}>Priority Item</span>
            </label>
            <small style={{ color: 'var(--color-text-muted)', fontSize: '12px', display: 'block', marginTop: '5px', marginLeft: '28px' }}>Priority items require confirmation when counted as zero quantity</small>
          </div>
          <div className="form-group">
            <label>Default item threshold</label>
            <input type="number" step="0.01" min="0" value={formData.valueThreshold || ''} onChange={(e) => setFormData({ ...formData, valueThreshold: e.target.value })} placeholder="e.g. 1000" />
            <small className="im-threshold-help">Used when no cluster-specific item threshold applies for the count site.</small>
          </div>
          <div className="im-cluster-threshold-section im-item-cluster-section">
            <button
              type="button"
              className="im-cluster-toggle"
              onClick={() => setItemClusterExpanded((v) => !v)}
              aria-expanded={itemClusterExpanded}
            >
              Cluster-specific item thresholds {itemClusterExpanded ? '▲' : '▼'}
            </button>
            {itemClusterExpanded && (
              <div className="im-item-cluster-panel">
                {siteClusters.length === 0 ? (
                  <p className="im-cluster-empty-msg">
                    No site clusters found. Add cluster values to site information to configure cluster-specific thresholds.
                  </p>
                ) : (
                  <>
                    <p className="im-threshold-help">
                      Item cluster thresholds override both item default and global thresholds for matching site clusters.
                    </p>
                    <div className="im-cluster-threshold-grid">
                      {siteClusters.map((cluster) => (
                        <div key={cluster.key} className="im-cluster-threshold-field">
                          <label htmlFor={`im-item-cluster-${cluster.key}`}>{cluster.label}</label>
                          <input
                            id={`im-item-cluster-${cluster.key}`}
                            type="number"
                            min="0"
                            step="0.01"
                            className="im-global-limit-input"
                            placeholder="Use default"
                            value={formData.valueThresholdsByCluster[cluster.key] ?? ''}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                valueThresholdsByCluster: {
                                  ...prev.valueThresholdsByCluster,
                                  [cluster.key]: e.target.value
                                }
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          {!editingItem && additionalBrandCandidates.length > 0 && (
            <div className="im-add-brands-section">
              <h5 className="im-add-brands-title">Add this item to other brands?</h5>
              <p className="im-add-brands-help">
                Select any additional brands that should also receive this new item.
              </p>
              <div className="im-add-brands-list">
                {additionalBrandCandidates.map((brand) => {
                  const id = String(brand.id);
                  const duplicateField = findNewItemDuplicateInBrand(allInventory, id, formData);
                  return (
                    <label key={id} className="im-add-brands-row">
                      <input
                        type="checkbox"
                        checked={additionalBrandIds.includes(id)}
                        onChange={() => toggleAdditionalBrand(id)}
                        disabled={createSaving}
                      />
                      <span className="im-add-brands-name">{brand.name}</span>
                      {duplicateField && (
                        <span className="im-add-brands-warning">
                          Already has an item with the same {duplicateField}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">{editingItem ? 'Update Item' : 'Create Item'}</button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
          </div>
        </form>
      )}

      {success && !showCreateForm && <div className="success" style={{ marginBottom: '20px' }}>{success}</div>}
      {error && !showCreateForm && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}

      <div className="card im-table-card">
        <div className="im-search-bar">
          <Search size={15} className="im-search-icon" />
          <input type="text" placeholder="Search by name, description, category, vendor, location, code..." className="im-search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          {searchTerm && <button type="button" onClick={() => setSearchTerm('')} className="im-search-clear">Clear</button>}
        </div>
        <div className="im-table-scroll">
          <table className="table im-table">
            <thead>
              <tr>
                <SortableHeader column="name">Product Name</SortableHeader>
                <SortableHeader column="packSize">Pack Size</SortableHeader>
                <SortableHeader column="unit1">Unit 1</SortableHeader>
                <SortableHeader column="unit2">Unit 2</SortableHeader>
                <SortableHeader column="location">Location</SortableHeader>
                <SortableHeader column="priority">Priority</SortableHeader>
                <SortableHeader column="valueThreshold">High Value</SortableHeader>
                <th className="im-actions-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {inventory.length === 0 ? (
                <tr>
                  <td colSpan="8" className="im-empty-state">No inventory items</td>
                </tr>
              ) : getSortedInventory().length === 0 ? (
                <tr>
                  <td colSpan="8" className="im-empty-state">No items match your search.</td>
                </tr>
              ) : (
                getSortedInventory().map(item => (
                  <tr key={item.id} className="im-row">
                    <td className="im-item-cell">
                      <div className="im-item-text">
                        <span className="im-item-name">{friendlyItemLabel(item)}</span>
                        {item.vendorItemCode && <span className="im-item-code">{item.vendorItemCode}</span>}
                      </div>
                    </td>
                    <td className="im-meta-cell">{item.packSize || '-'}</td>
                    <td className="im-unit-cell">
                      <div className="im-unit-text">
                        <span>{item.unit1 || '-'}</span>
                        <span className="im-unit-value">{item.value1 ? parseFloat(item.value1).toFixed(2) : '0.00'}</span>
                      </div>
                    </td>
                    <td className="im-unit-cell">
                      <div className="im-unit-text">
                        <span>{item.unit2 || '-'}</span>
                        <span className="im-unit-value">{item.value2 ? parseFloat(item.value2).toFixed(2) : '-'}</span>
                      </div>
                    </td>
                    <td className="im-meta-cell">{item.location || '-'}</td>
                    <td className="im-meta-cell" style={{ textAlign: 'center' }}>
                      {item.priority ? (<span className="im-priority-badge">Priority</span>) : '-'}
                    </td>
                    <td className="im-meta-cell">
                      {item.valueThreshold != null && item.valueThreshold !== '' ? parseFloat(item.valueThreshold).toFixed(2) : '-'}
                    </td>
                    <td>
                      <div className="im-actions">
                        <button type="button" className="im-action-btn im-action-edit" onClick={() => handleEdit(item)} title="Edit item">
                          <Pencil size={14} />
                        </button>
                        <button type="button" className="im-action-btn im-action-delete" onClick={() => handleDelete(item.id)} title="Delete item">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {multiBrandModal && (
        <div className="im-mb-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="im-mb-modal-title">
          <div className="im-mb-modal">
            <div className="im-mb-modal-header">
              <h4 id="im-mb-modal-title" className="im-mb-modal-title">Apply changes to other brands?</h4>
              <button
                type="button"
                className="im-mb-modal-close"
                onClick={closeMultiBrandModal}
                disabled={multiBrandSaving}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="im-mb-modal-body">
              <p className="im-mb-modal-text">
                This item also appears in other brands. Select where else to apply these changes.
              </p>
              <p className="im-mb-modal-warning">{multiBrandModal.warning}</p>
              <div className="im-mb-current">
                <strong>Current brand:</strong> {multiBrandModal.currentBrandName}
                <br />
                <strong>Item:</strong> {multiBrandModal.currentItemName}
                {multiBrandModal.currentVendorItemCode
                  ? ` (Code: ${multiBrandModal.currentVendorItemCode})`
                  : ''}
              </div>
              {multiBrandModal.changedSummary.length > 0 ? (
                <div className="im-mb-changed">
                  <strong>Changed fields</strong>
                  <ul>
                    {multiBrandModal.changedSummary.map((row) => (
                      <li key={row.field}>
                        {row.label}: {row.from} → {row.to}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="im-mb-modal-text">No copyable field changes detected vs the stored item.</p>
              )}
              <div className="im-mb-match-list">
                {multiBrandModal.matches.map((m) => {
                  const id = String(m.itemId);
                  const checked = multiBrandSelectedIds.includes(id);
                  return (
                    <label key={id} className="im-mb-match-row">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={multiBrandSaving}
                        onChange={() => toggleMultiBrandSelection(m.itemId)}
                      />
                      <span className="im-mb-match-meta">
                        <span className="im-mb-match-brand">{m.brandName || '(No Brand)'}</span>
                        <span className="im-mb-match-name">{m.itemName || '-'}</span>
                        <span className="im-mb-match-code">{m.vendorItemCode || '-'}</span>
                        <span className="im-mb-match-detail">
                          Value1: {m.value1 != null && m.value1 !== '' ? m.value1 : '-'}
                          {' · '}Pack: {m.packSize || '-'}
                          {' · '}Vendor: {m.vendor || '-'}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="im-mb-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeMultiBrandModal}
                disabled={multiBrandSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSaveCurrentBrandOnly}
                disabled={multiBrandSaving}
              >
                Save current brand only
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleApplyToSelectedBrands}
                disabled={multiBrandSaving || multiBrandSelectedIds.length === 0}
              >
                Apply to selected brands
              </button>
            </div>
          </div>
        </div>
      )}

      {createConfirm && (
        <div className="im-mb-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="im-mb-create-title">
          <div className="im-mb-modal im-mb-create-modal">
            <div className="im-mb-modal-header">
              <h4 id="im-mb-create-title" className="im-mb-modal-title">Create item in multiple brands</h4>
              <button
                type="button"
                className="im-mb-modal-close"
                onClick={cancelCreateConfirm}
                disabled={createSaving}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="im-mb-modal-body">
              <p className="im-mb-modal-text">
                This will create this new item in {createConfirm.brandNames.length} brands. Continue?
              </p>
              <div className="im-mb-current">
                <strong>Item:</strong> {createConfirm.submitData.itemName}
                {createConfirm.submitData.vendorItemCode
                  ? ` (Code: ${createConfirm.submitData.vendorItemCode})`
                  : ''}
              </div>
              <ul className="im-mb-create-brand-list">
                {createConfirm.brandNames.map((name, idx) => (
                  <li key={`${name}-${idx}`}>{name}</li>
                ))}
              </ul>
            </div>
            <div className="im-mb-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelCreateConfirm}
                disabled={createSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmMultiBrandCreate}
                disabled={createSaving}
              >
                {createSaving ? 'Creating…' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagement;